'use client';

/**
 * PAPER EXAM. A hand-written competition paper, sat on screen the way it is sat on paper.
 *
 * This is deliberately not MockExam. MockExam is the generated Kangaroo track with its own
 * scoring (start at 30, minus 1 for a wrong answer) and its own dark skin. A SOF paper has
 * no negative marking, gives 1 mark a question and 3 for an Achiever, and a child sits it
 * with a pencil and rough paper. So this screen has three things the Kangaroo one lacks:
 *
 *   1. a FIGURE next to the stem, inline SVG that the server has already allowlisted;
 *   2. the app's SCRATCHPAD, the same slide-up sheet every practice screen uses, because
 *      olympiad working is done by hand and a screen with nowhere to work forces a guess;
 *   3. the brand palette: cream page, white cards, coral CTAs. No dark screens.
 *
 * The figure is capped small and the stem and all five options are laid out to fit a 720 px
 * viewport, so nothing the child needs sits below the fold.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useScratchpad } from '@/components/mathtutor/Scratchpad';
import { useAuth } from '@/contexts/AuthContext';

interface PaperQuestion {
  id: number;
  display: string;
  // A choice is text, or a picture { svg, alt } already allowlisted by the server; a picture that
  // failed the check arrives as { alt } alone and is shown as its words.
  choices: (string | { svg?: string; alt: string })[];
  _answer: number;
  points: number;
  distractorReasons: string[];
  strategy: string;
  shape: string;                                   // the SOF section for a paper item
  figure?: { svg: string; alt: string } | null;
}

interface PaperData {
  questions: PaperQuestion[];
  totalQuestions: number;
  timeLimit: number;
  source: string;
}

interface PaperExamProps {
  paper: string;                                   // e.g. "grade8-paper-2"
  studentId?: number;
  onBack?: () => void;
  /**
   * How many questions a signed-out visitor may answer before the wall. Passed only by the
   * public marketing route; /dev/paper and the signed-in product leave it undefined and get the
   * whole paper. Google still indexes the shell and the first questions, which is the point:
   * enough to prove the papers are real, not enough to hand over 1,350 of them.
   */
  previewQuestions?: number;
  /**
   * Whether the sixty-minute clock runs. Default true, because a paper is a timed paper and
   * that is the thing being practised. A child entering from their own dashboard is offered
   * the choice, because a first sitting under a countdown teaches panic rather than method.
   * When false there is no clock, no colour change, and no auto-submit on time.
   */
  timed?: boolean;
  /**
   * Show the timer as a choice on the intro rather than a fact. Set when a child opens a paper
   * from their own dashboard: a first sitting under a countdown teaches panic, and the clock
   * matters on the second attempt. The public route leaves this off, where the paper is
   * presented as the timed thing it is.
   */
  offerTimerChoice?: boolean;
  /** Paper ids to offer as a switcher on the intro. Omit for a single fixed paper. */
  paperChoices?: string[];
  onPickPaper?: (paper: string) => void;
}

type Phase = 'loading' | 'intro' | 'exam' | 'results' | 'error';

const SECTION_LABEL: Record<string, string> = {
  logical_reasoning: 'Logical Reasoning',
  mathematical_reasoning: 'Mathematical Reasoning',
  everyday: 'Everyday Mathematics',
  achievers: 'Achievers',
};
const LETTERS = ['A', 'B', 'C', 'D', 'E'];

/** One answer option: plain text, or an inline picture with its alt text. */
function Choice({ c }: { c: string | { svg?: string; alt: string } }) {
  if (typeof c === 'string') return <span>{c}</span>;
  if (!c.svg) return <span>{c.alt}</span>;
  return <span className="block w-full max-w-[120px] rounded-lg border border-brand-cream-2 bg-white p-1 [&_svg]:block [&_svg]:h-auto [&_svg]:w-full" role="img" aria-label={c.alt} dangerouslySetInnerHTML={{ __html: c.svg }} />;
}

/** Wide pictures (a pictograph, a number line) get more width so their labels stay readable; the
 *  height stays under about 180 px either way, which keeps the options above the fold. */
function figureWidth(svg: string) {
  const m = svg.match(/viewBox="[\d.\s-]*?([\d.]+)\s+([\d.]+)"/);
  if (!m) return 'w-44';
  const aspect = Number(m[1]) / Number(m[2]);
  return aspect >= 1.6 ? 'w-72' : 'w-44';
}

const choiceText = (c: string | { svg?: string; alt: string }) => typeof c === 'string' ? c : c.alt;
// SOF gives every Level 1 paper sixty minutes, whatever its length.
const SOF_MINUTES = 60;

/** SOF marks for one question: Achievers carry 3 on a 50-question paper and 2 on a 35-question paper. */
function marksFor(q: PaperQuestion, total: number) {
  if (q.shape !== 'achievers') return 1;
  return total <= 35 ? 2 : 3;
}

function paperTitle(paper: string) {
  const m = paper.match(/^grade(\d)-paper-(\d)$/);
  return m ? `Grade ${m[1]} · Paper ${m[2]}` : paper;
}

/* ─────────────────────────── The exam ─────────────────────────── */

export default function PaperExam({
  paper, studentId, onBack, previewQuestions,
  timed = true, offerTimerChoice = false, paperChoices, onPickPaper,
}: PaperExamProps) {
  const { user } = useAuth();
  const locked = (n: number) => previewQuestions != null && !user && n >= previewQuestions;
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string>('');
  const [exam, setExam] = useState<PaperData | null>(null);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [current, setCurrent] = useState(0);
  const [timeLeft, setTimeLeft] = useState(SOF_MINUTES * 60 * 1000);
  const [timerOn, setTimerOn] = useState(timed);
  const [navOpen, setNavOpen] = useState(false);   // the 50-pill grid, folded away by default
  // Sitting persistence. Before this, answers lived only here: a closed tab at question 40
  // threw away all forty. sessionId is the open sitting on the server; resumable is one found
  // waiting when the paper is opened.
  const sessionId = useRef<number | null>(null);
  const [resumable, setResumable] = useState<{ id: number; answers: (number | null)[]; currentQuestion: number; timeLeftMs: number | null; timed: boolean } | null>(null);
  const startedAt = useRef(0);
  // The app's one scratch pad, the slide-up sheet every solve screen uses. Revealed only while
  // the paper is being sat, so the pencil is not on the intro or the results.
  useScratchpad(phase === 'exam');

  // Load the paper first, so the intro screen states real counts and marks.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiFetch('/api/kangaroo-math/mock/generate', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ source: 'handmade', paper }),
        });
        if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `HTTP ${res.status}`); }
        const data: PaperData = await res.json();
        if (!alive) return;
        setExam(data);
        setAnswers(new Array(data.totalQuestions).fill(null));
        // Is there a half-finished sitting waiting? Only for a signed-in child: an anonymous
        // visitor on the public route has nowhere to save to.
        if (studentId) {
          try {
            const r = await apiFetch(`/api/kangaroo-math/paper/${studentId}/${paper}/open`, { credentials: 'include' });
            if (r.ok) {
              const { session } = await r.json();
              if (alive && session && Array.isArray(session.answers) && session.answers.some((a: number | null) => a != null)) {
                setResumable(session);
              }
            }
          } catch { /* a missing sitting must never stop a child starting the paper */ }
        }
        setPhase('intro');
      } catch (e) {
        if (!alive) return;
        setError(e instanceof Error ? e.message : 'could not load the paper');
        setPhase('error');
      }
    })();
    return () => { alive = false; };
  }, [paper]);

  const submit = useCallback(async () => {
    if (!exam) return;
    setPhase('results');
    try {
      const res = await apiFetch('/api/kangaroo-math/mock/score', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ answers, exam, studentId: studentId || undefined, duration: Date.now() - startedAt.current }),
      });
      // The server also returns its diagnosis (postMortem). The component that draws it lives
      // with the Kangaroo screen and is not on main yet, so this screen shows the marks and the
      // per-question review only; the score call still records the sitting.
      await res.text();
      // Close the sitting so it stops being offered as resumable.
      if (studentId && sessionId.current) {
        await apiFetch(`/api/kangaroo-math/paper/${studentId}/${sessionId.current}/submit`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ answers }),
        }).catch(() => {});
        sessionId.current = null;
      }
    } catch { /* the marks are computed here; recording the sitting is best effort */ }
  }, [exam, answers, studentId]);

  // Timer
  useEffect(() => {
    if (phase !== 'exam' || !timerOn) return;    // untimed sittings never auto-submit
    const t = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1000) { clearInterval(t); submit(); return 0; }
        return prev - 1000;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [phase, submit, timerOn]);

  // Autosave. Fires whenever an answer changes, never blocks the child, and silently does
  // nothing when there is no sitting to save to.
  useEffect(() => {
    if (phase !== 'exam' || !studentId || !sessionId.current) return;
    const id = setTimeout(() => {
      apiFetch(`/api/kangaroo-math/paper/${studentId}/${sessionId.current}/save`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ answers, currentQuestion: current, timeLeftMs: timerOn ? timeLeft : null }),
      }).catch(() => { /* best effort: a failed save must not interrupt a paper */ });
    }, 400);                                  // debounce, so tapping through options is one write
    return () => clearTimeout(id);
  }, [answers, current, phase, studentId, timerOn, timeLeft]);

  const fmt = (ms: number) => { const s = Math.max(0, Math.floor(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

  /* ── loading / error ── */
  if (phase === 'loading') return <div className="min-h-screen bg-brand-cream" />;
  if (phase === 'error' || !exam) {
    return (
      <div className="min-h-screen bg-brand-cream p-8 text-brand-ink">
        <p className="font-kids-title text-2xl font-bold">This paper could not be loaded.</p>
        <p className="mt-2 text-brand-ink-soft">{error}</p>
        {onBack && <button onClick={onBack} className="mt-6 rounded-full bg-brand-coral px-5 py-2 font-bold text-white">Back</button>}
      </div>
    );
  }

  const total = exam.totalQuestions;
  const totalMarks = exam.questions.reduce((s, q) => s + marksFor(q, total), 0);
  const sections = exam.questions.reduce<Record<string, number>>((acc, q) => { acc[q.shape] = (acc[q.shape] || 0) + 1; return acc; }, {});

  /* ── intro ── */
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-brand-cream px-6 py-10 text-brand-ink">
        <div className="relative mx-auto max-w-2xl overflow-hidden rounded-3xl border-2 border-brand-cream-2 bg-white shadow-lg">
          {/* coral banner: the medal makes it read as a competition rather than a worksheet */}
          <div className="relative bg-gradient-to-r from-brand-coral to-brand-tangerine px-8 py-7 text-white">
            <div className="pointer-events-none absolute -right-6 -top-8 text-[7rem] opacity-20 select-none" aria-hidden>🏅</div>
            {onBack && (
              <button onClick={onBack} aria-label="Close and go back"
                className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/25 text-xl font-bold leading-none text-white backdrop-blur transition hover:bg-white/40">
                ×
              </button>
            )}
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/85">Competition Math</p>
            <h1 className="font-kids-title mt-1 text-4xl font-bold drop-shadow-sm">{paperTitle(paper)}</h1>
          </div>

          <div className="p-8 pt-6">
          <div className="grid grid-cols-3 gap-3 text-center text-sm">
            <div className="rounded-2xl border border-brand-cream-2 bg-brand-cream p-4"><div className="font-kids-title text-3xl text-brand-coral-deep">{total}</div><div className="mt-0.5 text-xs font-semibold text-brand-ink-soft">questions</div></div>
            <div className="rounded-2xl border border-brand-cream-2 bg-brand-cream p-4"><div className="font-kids-title text-3xl text-brand-coral-deep">{totalMarks}</div><div className="mt-0.5 text-xs font-semibold text-brand-ink-soft">marks</div></div>
            <div className="rounded-2xl border border-brand-cream-2 bg-brand-cream p-4"><div className="font-kids-title text-3xl text-brand-coral-deep">{timerOn ? SOF_MINUTES : '∞'}</div><div className="mt-0.5 text-xs font-semibold text-brand-ink-soft">{timerOn ? 'minutes' : 'no limit'}</div></div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {Object.entries(sections).map(([s, n]) => (
              <span key={s} className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                s === 'achievers'
                  ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-300'
                  : 'bg-brand-cream-2 text-brand-ink-soft'}`}>
                {n} × {SECTION_LABEL[s] || s}
                {s === 'achievers' ? ` · ${marksFor({ shape: 'achievers' } as PaperQuestion, total)} marks each` : ''}
              </span>
            ))}
          </div>
          <ul className="mt-4 space-y-1 text-sm text-brand-ink-soft">
            <li>No negative marking. A wrong answer scores 0, the same as a skip.</li>
            <li>Tap the pencil at the bottom right for the scratchpad.</li>
          </ul>
          {paperChoices && paperChoices.length > 1 && onPickPaper && (
            <div className="mt-6">
              <div className="mb-2 text-sm font-bold text-brand-ink-soft">Which paper?</div>
              <div className="flex flex-wrap gap-2">
                {paperChoices.map((id, i) => (
                  <button key={id} onClick={() => onPickPaper(id)}
                    className={`rounded-full px-4 py-2 text-sm font-bold transition ${
                      id === paper ? 'bg-brand-coral text-white shadow'
                        : 'border border-brand-cream-2 bg-white text-brand-ink-soft hover:border-brand-coral'}`}>
                    Paper {i + 1}
                  </button>
                ))}
              </div>
            </div>
          )}

          {resumable && (
            /* The half-finished sitting, offered before the timer choice because it decides
               whether the timer choice is even relevant. Says how far they got, so "carry on"
               is a real decision rather than a guess. */
            <div className="mt-6 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5">
              <div className="font-bold text-amber-900">
                You did not finish this paper last time
              </div>
              <p className="mt-1 text-sm text-amber-900/80">
                {resumable.answers.filter(a => a != null).length} of {total} questions answered
                {resumable.timed && resumable.timeLeftMs != null
                  ? `, with ${Math.ceil(resumable.timeLeftMs / 60000)} minutes left on the clock.`
                  : '.'}
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    sessionId.current = resumable.id;
                    setAnswers(resumable.answers);
                    setCurrent(resumable.currentQuestion || 0);
                    setTimerOn(resumable.timed);
                    if (resumable.timed && resumable.timeLeftMs != null) setTimeLeft(resumable.timeLeftMs);
                    startedAt.current = Date.now();
                    setResumable(null);
                    setPhase('exam');
                  }}
                  className="rounded-full bg-gradient-to-r from-brand-coral to-brand-tangerine px-6 py-2.5 font-bold text-white shadow-md hover:opacity-90">
                  Carry on where I left off
                </button>
                <button
                  onClick={async () => {
                    // Throw the old sitting away rather than leave two to choose between.
                    if (studentId) {
                      await apiFetch(`/api/kangaroo-math/paper/${studentId}/${resumable.id}`, {
                        method: 'DELETE', credentials: 'include',
                      }).catch(() => {});
                    }
                    setResumable(null);
                  }}
                  className="rounded-full border-2 border-amber-300 px-5 py-2.5 font-bold text-amber-900 hover:bg-amber-100">
                  Start again
                </button>
              </div>
            </div>
          )}

          {offerTimerChoice && (
            <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-2xl bg-brand-cream px-4 py-3">
              <input type="checkbox" checked={timerOn} onChange={e => setTimerOn(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 accent-[#E8484C]" />
              <span>
                <span className="block font-bold">Keep the {SOF_MINUTES} minute timer</span>
                <span className="block text-sm text-brand-ink-soft">
                  Turn it off to take as long as you like. Nothing is submitted for you.
                </span>
              </span>
            </label>
          )}

          <div className="mt-8 flex gap-3">
            <button onClick={async () => {
              startedAt.current = Date.now();
              if (studentId && exam) {
                try {
                  const r = await apiFetch(`/api/kangaroo-math/paper/${studentId}/${paper}/start`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                    body: JSON.stringify({ totalQuestions: exam.totalQuestions, timed: timerOn, timeLeftMs: timerOn ? timeLeft : null }),
                  });
                  if (r.ok) sessionId.current = (await r.json()).sessionId ?? null;
                } catch { /* a paper must start even if the sitting cannot be recorded */ }
              }
              setPhase('exam');
            }}
              className="rounded-full bg-gradient-to-r from-brand-coral to-brand-tangerine px-8 py-3 font-bold text-white shadow-md hover:opacity-90">
              Start the paper
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── results ── */
  if (phase === 'results') {
    let marks = 0, correct = 0, wrong = 0, skipped = 0;
    const bySection: Record<string, { got: number; of: number }> = {};
    exam.questions.forEach((q, i) => {
      const m = marksFor(q, total);
      bySection[q.shape] = bySection[q.shape] || { got: 0, of: 0 };
      bySection[q.shape].of += m;
      if (answers[i] == null) skipped++;
      else if (answers[i] === q._answer) { correct++; marks += m; bySection[q.shape].got += m; }
      else wrong++;
    });
    return (
      <div className="min-h-screen bg-brand-cream px-6 py-10 text-brand-ink">
        <div className="mx-auto max-w-3xl space-y-6">
          <div className="rounded-3xl border-2 border-brand-cream-2 bg-white p-8 shadow-sm">
            <p className="text-sm font-bold uppercase tracking-widest text-brand-coral">{paperTitle(paper)}</p>
            <h1 className="font-kids-title mt-1 text-5xl font-bold">{marks} <span className="text-2xl text-brand-ink-soft">/ {totalMarks} marks</span></h1>
            <div className="mt-4 flex flex-wrap gap-2 text-sm font-bold">
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">{correct} correct</span>
              <span className="rounded-full bg-rose-50 px-3 py-1 text-rose-700">{wrong} wrong</span>
              <span className="rounded-full bg-brand-cream-2 px-3 py-1 text-brand-ink-soft">{skipped} skipped</span>
            </div>
            <table className="mt-5 w-full text-sm">
              <tbody>
                {Object.entries(bySection).map(([s, v]) => (
                  <tr key={s} className="border-t border-brand-cream-2">
                    <td className="py-2 text-brand-ink-soft">{SECTION_LABEL[s] || s}</td>
                    <td className="py-2 text-right font-bold">{v.got} / {v.of}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


          <div className="space-y-3">
            {exam.questions.map((q, i) => {
              const picked = answers[i];
              const ok = picked === q._answer;
              return (
                <div key={q.id} className="rounded-2xl border-2 border-brand-cream-2 bg-white p-5">
                  <div className="mb-2 flex items-center gap-2 text-xs font-bold">
                    <span className="text-brand-ink-soft">Q{i + 1}</span>
                    <span className={`rounded-full px-2.5 py-0.5 ${q.shape === 'achievers' ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-300' : 'bg-brand-cream-2 text-brand-ink-soft'}`}>{SECTION_LABEL[q.shape] || q.shape}</span>
                    <span className={`rounded-full px-2 py-0.5 ${ok ? 'bg-emerald-50 text-emerald-700' : picked == null ? 'bg-brand-cream-2 text-brand-ink-soft' : 'bg-rose-50 text-rose-700'}`}>
                      {ok ? 'Correct' : picked == null ? 'Skipped' : 'Wrong'}
                    </span>
                  </div>
                  <div className="flex flex-col gap-4 sm:flex-row">
                    {q.figure && <div className="w-32 shrink-0 rounded-xl border border-brand-cream-2 bg-white p-1 [&_svg]:block [&_svg]:h-auto [&_svg]:w-full" role="img" aria-label={q.figure.alt} dangerouslySetInnerHTML={{ __html: q.figure.svg }} />}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold">{q.display}</p>
                      <p className="mt-2 text-sm"><span className="font-bold text-emerald-700">Answer: {choiceText(q.choices[q._answer])}</span>
                        {picked != null && !ok && <span className="ml-3 text-rose-700">You chose {choiceText(q.choices[picked])}</span>}</p>
                      {picked != null && !ok && q.distractorReasons[picked] && <p className="mt-1 text-sm text-brand-ink-soft">{q.distractorReasons[picked]}</p>}
                      {q.strategy && <p className="mt-2 rounded-xl bg-brand-cream-2 p-3 text-sm"><span className="font-bold text-brand-coral-deep">How to see it: </span>{q.strategy}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {onBack && <button onClick={onBack} className="rounded-full bg-brand-coral px-6 py-3 font-bold text-white">Done</button>}
        </div>
      </div>
    );
  }

  /* ── exam ── */
  const q = exam.questions[current];
  const answered = answers.filter(a => a != null).length;
  const wall = locked(current);

  return (
    <div className="min-h-screen bg-brand-cream text-brand-ink">
      {/* Top bar. Fifty numbered pills used to eat the top third of the screen before a child
          had read a word of question one, so the grid folds away and the bar carries the two
          things that matter while sitting a paper: time left and how far along. */}
      <div className="sticky top-0 z-20 shadow-sm">
        <div className="bg-gradient-to-r from-brand-coral to-brand-tangerine text-white">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-2.5">
            <div className="flex min-w-0 items-center gap-2">
              {onBack && (
                <button onClick={onBack} aria-label="Leave the paper"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/20 text-lg font-bold leading-none text-white transition hover:bg-white/35">
                  ×
                </button>
              )}
              <span className="min-w-0 truncate text-sm font-bold text-white/90">{paperTitle(paper)}</span>
            </div>
            {timerOn
              ? <span className={`font-mono text-xl font-bold tabular-nums ${timeLeft < 5 * 60 * 1000 ? 'animate-pulse text-amber-200' : ''}`}>{fmt(timeLeft)}</span>
              : <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-bold">no timer</span>}
            <button onClick={() => setNavOpen(o => !o)}
              className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-bold transition hover:bg-white/30">
              {answered}/{total}
              <span aria-hidden>{navOpen ? '\u25b4' : '\u25be'}</span>
            </button>
          </div>
          {/* how much of the paper is answered, at a glance */}
          <div className="h-1 w-full bg-white/25">
            <div className="h-full bg-white transition-all duration-300" style={{ width: `${(answered / total) * 100}%` }} />
          </div>
        </div>

        {navOpen && (
          <div className="border-b border-brand-cream-2 bg-white">
            <div className="mx-auto max-w-3xl px-4 py-3">
              <div className="flex flex-wrap justify-center gap-1.5">
                {exam.questions.map((_, i) => (
                  <button key={i} onClick={() => { setCurrent(i); setNavOpen(false); }}
                    title={locked(i) ? 'Sign in to unlock the rest of the paper' : undefined}
                    className={`h-8 w-8 shrink-0 rounded-lg text-xs font-bold transition ${i === current ? 'bg-brand-coral text-white shadow' : locked(i) ? 'border border-dashed border-brand-cream-2 text-brand-ink-soft/50' : answers[i] != null ? 'bg-emerald-100 text-emerald-800' : 'border border-brand-cream-2 text-brand-ink-soft hover:border-brand-coral'}`}>
                    {locked(i) ? '\u00b7' : i + 1}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-center text-xs text-brand-ink-soft">Green means answered. Tap any number to jump.</p>
            </div>
          </div>
        )}
      </div>

      {/* pb-28 keeps the Previous/Next row clear of the floating scratchpad button, which on a
          phone sat over the top of Next. The centre stayed tappable, but the top of the
          button did not, which is the half a child aiming quickly is most likely to hit. */}
      <div className="mx-auto max-w-3xl px-4 py-4 pb-28 sm:pb-4">
        {wall ? (
          /* The preview is over. Everything above stays on screen, so it is obvious what is
             being unlocked rather than the paper simply vanishing. */
          <div className="rounded-3xl border-2 border-brand-cream-2 bg-white p-8 shadow-sm text-center">
            <div className="text-4xl" aria-hidden="true">🔒</div>
            <h2 className="font-kids-title text-2xl text-brand-ink mt-3">
              That&apos;s the preview
            </h2>
            <p className="text-brand-ink-soft mt-3 max-w-md mx-auto">
              That was the first {previewQuestions} of {total} questions. Sign in to finish this
              paper, see your score section by section, and open all 30 papers.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href="/auth"
                className="rounded-full bg-gradient-to-r from-brand-coral to-brand-tangerine px-7 py-3 font-bold text-white shadow-md hover:opacity-90">
                Create an account to continue
              </a>
              <button onClick={() => setCurrent(0)}
                className="rounded-full border-2 border-brand-cream-2 px-6 py-3 font-bold text-brand-ink-soft hover:bg-brand-cream-2">
                Back to question 1
              </button>
            </div>
            <p className="text-brand-ink-soft text-xs mt-5">
              Already have an account? <a href="/auth" className="underline font-semibold">Sign in</a>.
            </p>
          </div>
        ) : (
        /* question card */
        <div className="rounded-3xl border-2 border-brand-cream-2 bg-white p-6 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-xs font-bold">
            <span className="font-kids-title text-lg text-brand-ink">Question {current + 1}</span>
            <span className={`rounded-full px-2.5 py-0.5 ${q.shape === 'achievers' ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-300' : 'bg-brand-cream-2 text-brand-ink-soft'}`}>{SECTION_LABEL[q.shape] || q.shape}</span>
            <span className="rounded-full bg-brand-cream-2 px-2 py-0.5 text-brand-ink-soft">{marksFor(q, total)} {marksFor(q, total) === 1 ? 'mark' : 'marks'}</span>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            {q.figure && (
              <div className={`${figureWidth(q.figure.svg)} shrink-0 self-center rounded-xl border border-brand-cream-2 bg-white p-2 [&_svg]:block [&_svg]:h-auto [&_svg]:w-full sm:self-start`}
                role="img" aria-label={q.figure.alt} dangerouslySetInnerHTML={{ __html: q.figure.svg }} />
            )}
            <p className="text-lg font-semibold leading-relaxed">{q.display}</p>
          </div>
          {/* Text options stack. Picture options go five across so the set stays above the fold,
              but only once there is room: on a 390px phone five across renders each one at about
              40px, where a punched hole is a pinprick and three options look identical. Three
              across roughly doubles that. */}
          {(() => {
            const pictures = q.choices.some(c => typeof c !== 'string');
            return (
              <div className={pictures ? 'mt-4 grid grid-cols-3 sm:grid-cols-5 gap-2' : 'mt-4 space-y-2'}>
                {q.choices.map((choice, idx) => {
                  const sel = answers[current] === idx;
                  const toggle = () => setAnswers(a => { const n = [...a]; n[current] = sel ? null : idx; return n; });
                  const badge = <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${sel ? 'bg-brand-coral text-white' : 'bg-brand-cream-2 text-brand-ink-soft'}`}>{LETTERS[idx]}</span>;
                  if (pictures) {
                    return (
                      <button key={idx} onClick={toggle}
                        className={`flex flex-col items-center gap-2 rounded-xl border-2 p-2 transition ${sel ? 'border-brand-coral bg-brand-cream-2' : 'border-brand-cream-2 bg-white hover:border-brand-tangerine'}`}>
                        {badge}
                        <Choice c={choice} />
                      </button>
                    );
                  }
                  return (
                    <button key={idx} onClick={toggle}
                      className={`flex w-full items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left font-semibold transition ${sel ? 'border-brand-coral bg-brand-cream-2' : 'border-brand-cream-2 bg-white hover:border-brand-tangerine'}`}>
                      {badge}
                      <Choice c={choice} />
                    </button>
                  );
                })}
              </div>
            );
          })()}
          <div className="mt-5 flex items-center justify-between">
            <button onClick={() => setCurrent(c => Math.max(0, c - 1))} disabled={current === 0}
              className="rounded-full border-2 border-brand-cream-2 px-5 py-2 font-bold text-brand-ink-soft disabled:opacity-40 hover:bg-brand-cream-2">Previous</button>
            {current < total - 1 ? (
              <button onClick={() => setCurrent(c => Math.min(total - 1, c + 1))}
                className="rounded-full bg-gradient-to-r from-brand-coral to-brand-tangerine px-6 py-2 font-bold text-white shadow-md hover:opacity-90">Next</button>
            ) : (
              <button onClick={() => { if (answered < total && !window.confirm(`${total - answered} question${total - answered === 1 ? '' : 's'} unanswered. Submit anyway?`)) return; submit(); }}
                className="rounded-full bg-gradient-to-r from-brand-coral to-brand-tangerine px-6 py-2 font-bold text-white shadow-md hover:opacity-90">Submit paper</button>
            )}
          </div>
        </div>
        )}

      </div>
    </div>
  );
}
